import {
  FileNode,
  FileNodePermissions,
  FilePermissionType,
  IProjectUser,
  ISecurityGroup,
  IUser,
  IUserGroup,
  SecurityPermissionLevel,
} from '../../api-client/autogenerated';
import { Project } from '../../features/project/project';
import { sortLastNamesDesc } from '../../scripts/name-utils';

/**
 * Pure derivation for DriveAccessDialog: the project roster grouped into the three access
 * sections, seeded from the node's current shares.
 */

/**
 * Whether the user administers CENTERLINE Drive for the project: site admin, the subscriber's
 * admin, or a security group granting designTabPermission = Admin. Pass the group from
 * getCurrentSecurityGroup, which already resolves the company-view fallback, so this mirrors
 * the backend's userIsDriveAdmin cohorts. Admin access is role-derived - the server no longer
 * materializes admin share rows on each node - so node permissions alone don't reveal it.
 */
export const isDriveAdmin = (
  security?: ISecurityGroup | null,
  user?: IUser,
  currentProject?: Project,
): boolean => {
  if (user?.isSiteAdmin) return true;
  if (
    user?.adminOfSubscriberId &&
    currentProject &&
    user.adminOfSubscriberId === currentProject.subscriberId
  ) {
    return true;
  }
  return security?.designTabPermission === SecurityPermissionLevel.NUMBER_4;
};

const groupContainsUser = (userGroup: IUserGroup, userId: string) =>
  !!userGroup.userGroupUserList?.some((userGroupUser) => userGroupUser.userId === userId);

/**
 * 'automatic' covers access nobody grants on this node - the creator, Drive admins by security
 * group, and users reaching it through a share on a parent folder. 'grantable' is the editable
 * middle: users (and groups) whose security group makes them eligible. 'none' cannot be given
 * access here at all.
 */
export type DriveAccessSection = 'automatic' | 'grantable' | 'none';

export interface DriveAccessRow {
  userId: string;
  name: string;
  companyName?: string;
  section: DriveAccessSection;
  isOwner: boolean;
  isSelf: boolean;
  /**
   * Why they are in the automatic section, most specific to this item first: they made it, their
   * role reaches everything, or a parent folder's share reaches here. Rendered as the dialog's
   * Access Source column.
   */
  automaticReason?: 'creator' | 'admin' | 'inherited';
  /** The user's own share on exactly this node; null when they have none. */
  direct: { read: boolean; write: boolean; admin: boolean } | null;
  /** Shares reaching this node from ancestor folders - revocable only at the ancestor. */
  inheritedFrom: { folderName: string; permissions: FilePermissionType[] }[];
}

/**
 * The user's design-tab permission, resolved the way the backend's checkDesignTabAccess does:
 * their project security group, else - for members of the subscriber's company - the
 * subscriber's company-view group.
 */
const designTabPermissionFor = (
  projectUser: IProjectUser,
  securityGroups: ISecurityGroup[],
  project: Project | null | undefined,
): number | null | undefined => {
  if (projectUser.securityGroupId) {
    return securityGroups.find((sg) => sg.id === projectUser.securityGroupId)?.designTabPermission;
  }
  const subscriber = project?.subscriber;
  if (
    subscriber?.companyId &&
    projectUser.user?.companyId === subscriber.companyId &&
    subscriber.companyViewSecurityGroupId
  ) {
    return securityGroups.find((sg) => sg.id === subscriber.companyViewSecurityGroupId)
      ?.designTabPermission;
  }
  return null;
};

const isRoleAdmin = (
  projectUser: IProjectUser,
  securityGroups: ISecurityGroup[],
  project: Project | null | undefined,
): boolean => {
  const user = projectUser.user;
  return !!(
    user?.isSiteAdmin ||
    (user?.adminOfSubscriberId && user.adminOfSubscriberId === project?.subscriberId) ||
    designTabPermissionFor(projectUser, securityGroups, project) ===
      SecurityPermissionLevel.NUMBER_4
  );
};

const userEntriesFor = (node: FileNode | undefined, userId: string): FileNodePermissions[] =>
  node?.fileNodePermissions?.filter((p) => p.userIdTarget === userId) || [];

export const deriveDriveAccessRows = (
  node: FileNode | undefined,
  allProjectUsers: IProjectUser[],
  securityGroups: ISecurityGroup[],
  project: Project | null | undefined,
  currentUserId: string,
): DriveAccessRow[] => {
  const rows = allProjectUsers
    // Site admins administer every project's Drive without being granted or shown access on any
    // one of them, so they never appear as a row here - not even in the automatic section.
    .filter((pu) => pu.user && !pu.user.isSiteAdmin)
    .map(
      (pu): DriveAccessRow => {
        const entries = userEntriesFor(node, pu.userId);
        const directEntries = entries.filter((p) => !p.parentFolderSharedName);
        const direct = directEntries.length
          ? {
              read: directEntries.some((p) => p.permissions.includes(FilePermissionType.Read)),
              write: directEntries.some((p) => p.permissions.includes(FilePermissionType.Modify)),
              admin: directEntries.some((p) => p.permissions.includes(FilePermissionType.Delete)),
            }
          : null;
        const inheritedFrom = entries
          .filter((p) => p.parentFolderSharedName)
          .map((p) => ({ folderName: p.parentFolderSharedName!, permissions: p.permissions }));
        // Access that arrives on its own outranks eligibility: an admin, or anyone a parent
        // folder already reaches, cannot be granted or revoked on this node.
        const roleAdmin = isRoleAdmin(pu, securityGroups, project);
        const eligible = designTabPermissionFor(pu, securityGroups, project) != null;
        const isOwner = node?.ownerId === pu.userId;
        // Creator leads: they made the item, and no grant here adds to or takes from that, so
        // saying "Creator" explains their access better than the role or folder that also
        // happens to reach them. Ineligibility still outranks it, and an inherited share, for
        // the same reason: without Drive access in their security group the server refuses
        // them anyway, so a share row reaching them grants nothing.
        const automaticReason: DriveAccessRow['automaticReason'] =
          isOwner && (eligible || roleAdmin)
            ? 'creator'
            : roleAdmin
            ? 'admin'
            : eligible && inheritedFrom.length
            ? 'inherited'
            : undefined;
        const section: DriveAccessSection = automaticReason
          ? 'automatic'
          : eligible
          ? 'grantable'
          : 'none';
        return {
          userId: pu.userId,
          name: pu.user!.name,
          companyName: pu.user!.company?.name,
          section,
          automaticReason,
          isOwner,
          isSelf: pu.userId === currentUserId,
          direct,
          inheritedFrom,
        };
      },
    );
  // Sections in display order, the owner leading theirs, everyone else by last name - the
  // same comparator the rest of the app sorts people with, including the group member lists
  // inside this dialog. The comparator is a total order so Array.sort groups reliably.
  const sectionRank: Record<DriveAccessSection, number> = { automatic: 0, grantable: 1, none: 2 };
  return rows.sort((a, b) => {
    if (a.section !== b.section) return sectionRank[a.section] - sectionRank[b.section];
    if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
    return sortLastNamesDesc(a.name, b.name);
  });
};

export interface DriveAccessGroupRow {
  groupId: string;
  name: string;
  members: IUser[];
  /** The group's own share on exactly this node; null when it has none. */
  direct: { read: boolean; write: boolean; admin: boolean } | null;
}

/**
 * The project's user groups as editable rows, seeded from the group shares on this node.
 * Groups sort above the individual users in the eligible section.
 */
export const deriveDriveAccessGroups = (
  node: FileNode | undefined,
  groups: IUserGroup[],
): DriveAccessGroupRow[] =>
  groups
    .map(
      (group): DriveAccessGroupRow => {
        const entries = (node?.fileNodePermissions || []).filter(
          (p) => p.userGroupIdTarget === group.id && !p.parentFolderSharedName,
        );
        return {
          groupId: group.id,
          name: group.name,
          members:
            group.userGroupUserList
              ?.map((groupUser) => groupUser.user)
              .filter((u): u is IUser => !!u) || [],
          direct: entries.length
            ? {
                read: entries.some((p) => p.permissions.includes(FilePermissionType.Read)),
                write: entries.some((p) => p.permissions.includes(FilePermissionType.Modify)),
                admin: entries.some((p) => p.permissions.includes(FilePermissionType.Delete)),
              }
            : null,
        };
      },
    )
    .sort((a, b) => a.name.localeCompare(b.name));

export interface DrivePermissionTriple {
  read: boolean;
  write: boolean;
  admin: boolean;
}

/**
 * What a member ends up with: their own permissions, raised by any group they belong to that
 * holds a stronger one. A group grant cannot be taken away on the member's own row, so the
 * higher of the two is what the row shows.
 */
export const combineWithGroupPermissions = (
  own: DrivePermissionTriple,
  userId: string,
  groups: IUserGroup[],
  permissionsByGroupId: Record<string, DrivePermissionTriple>,
): DrivePermissionTriple =>
  groups.reduce((combined, group) => {
    if (!groupContainsUser(group, userId)) return combined;
    const groupPermissions = permissionsByGroupId[group.id];
    if (!groupPermissions) return combined;
    return {
      read: combined.read || groupPermissions.read,
      write: combined.write || groupPermissions.write,
      admin: combined.admin || groupPermissions.admin,
    };
  }, own);

/**
 * Who may change access on a node: its owner, a CENTERLINE Drive admin (role-derived - the
 * server no longer materializes admin share rows), or a holder of an Admin share on the node
 * or an ancestor (ancestor entries are present in the node's decoration). Never on the
 * synthesized traversal-only ancestors of a shared node, and never on the root.
 */
export const canManageDriveAccess = (
  node: FileNode | undefined,
  user: IUser,
  security: ISecurityGroup | null | undefined,
  project: Project | null | undefined,
  groups: IUserGroup[],
): boolean => {
  if (!node || node.traversalOnly || node.fullKey === '/') return false;
  if (node.ownerId === user.id) return true;
  if (isDriveAdmin(security, user, project)) return true;
  return !!node.fileNodePermissions?.some((p) => {
    if (!p.permissions.includes(FilePermissionType.Delete)) return false;
    if (p.shareToEveryone) return true;
    if (p.userIdTarget === user.id) return true;
    const groupTarget = groups.find((g) => g.id === p.userGroupIdTarget);
    return groupTarget ? groupContainsUser(groupTarget, user.id) : false;
  });
};
